/**
 * Phase 45 Plan 45-05 — StatusPill for portfolio rows.
 *
 * Visual: inline-flex pill with semantic dot + bg + fg per
 * UI-SPEC §Status palette. Inline-style colour flow per
 * Pill.tsx anti-pattern.
 *
 * A11y: carries `aria-label="Status: {status}"` since the dot is
 * colour-only (UI-SPEC §A11y Contract).
 */
import type { PortfolioStatus } from '../../services/api/portfolio';

interface PaletteEntry {
  bg: string;
  fg: string;
  dot: string;
}

const PALETTE: Record<PortfolioStatus, PaletteEntry> = {
  'On track': { bg: '#E8F5E9', fg: '#1B5E20', dot: '#10b981' },
  'At risk': { bg: '#FFF3E8', fg: '#9A4B12', dot: '#F59E0B' },
  'Off track': { bg: '#FEE4E2', fg: '#992517', dot: '#EF4444' },
  'On hold': { bg: '#F4F1EC', fg: '#5C5C68', dot: '#8E8E98' },
  Done: { bg: '#E0EAFF', fg: '#1E40AF', dot: '#3B82F6' },
};

export interface StatusPillProps {
  status: PortfolioStatus;
  className?: string;
}

export function StatusPill({ status, className = '' }: StatusPillProps) {
  const palette = PALETTE[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-semibold leading-none ${className}`}
      style={{ background: palette.bg, color: palette.fg }}
      aria-label={`Status: ${status}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: palette.dot }}
        aria-hidden="true"
      />
      {status}
    </span>
  );
}
